import { toNumber } from 'lodash';
import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AssemblyJourneys,
  CodJourney,
  SectionJourney,
} from 'src/constants/assemblyJourneys';
import { PATHS } from 'src/constants/paths';
import {
  isCurrentScreenWithCOD,
  isCurrentScreenWithCODThankYou,
  isCurrentScreenWithDYR,
} from 'src/utils';
import { checkSectionTypes } from 'src/utils/section-types-helper';

import { useLayoutsEditSidebar } from './LayoutEditSidebar.hook';
import type { LayoutsEditLayoutsPageSidebarProps } from './LayoutEditSidebar.interface';
import { Container } from './LayoutEditSidebar.styled';
import { LayoutEditSidebarAccordion } from './layoutEditSidebarAccordion/LayoutEditSidebarAccordion';
import { LayoutEditSidebarDrawer } from './layoutEditSidebarDrawer/LayoutEditSidebarDrawer';

export const LayoutEditSidebar = ({
  setActiveAssemblyJourney,
  globalComponentsScreen,
  ...props
}: LayoutsEditLayoutsPageSidebarProps) => {
  const {
    openDefaultDrawer,
    onToggleDefaultDrawer,
    openTakeoverDrawer,
    setOpenTakeoverDrawer,
    openScreenRotationDrawer,
    setOpenScreenRotationDrawer,
  } = useLayoutsEditSidebar();
  const navigate = useNavigate();

  useEffect(() => {
    if (openScreenRotationDrawer) {
      setActiveAssemblyJourney(SectionJourney.SECTION_ROTATION);
    } else if (openTakeoverDrawer) {
      setActiveAssemblyJourney(SectionJourney.TAKE_OVER);
    } else {
      setActiveAssemblyJourney(CodJourney.CUSTOMER_ENTERS_DRIVE_THRU);
    }
  }, [openScreenRotationDrawer, openTakeoverDrawer, setActiveAssemblyJourney]);

  const hasScreenRotationEvent = useMemo(() => {
    return props?.screens?.some((screen) =>
      screen.sections.some(
        (section) => section.config?.events?.SECTION_TIMER !== undefined,
      ),
    );
  }, [props.screens]);

  const handleNavigateToCreateSections = (index: number) => {
    navigate({
      pathname: PATHS.ASSEMBLY_LAYOUTS_CREATE_SECTIONS(props.layoutId),
      search: `?screen=${index}`,
    });
  };

  const onToggleTakeoverDrawer = () => {
    setOpenTakeoverDrawer((isOpen: boolean) => !isOpen);
  };

  const selectedScreens = props.screens?.map(
    (_, idx) => props.formMethods.watch(`screen_${idx}`) ?? false,
  );

  const noTakeoverSelected = selectedScreens.every((value) => value === false);

  const isScreenDisabled = (index: number) => {
    if (noTakeoverSelected) {
      return false;
    }

    const firstSelectedIndex = selectedScreens.findIndex(
      (value) => value === true,
    );
    const lastSelectedIndex = selectedScreens.lastIndexOf(true);

    if (selectedScreens[index]) {
      return index !== firstSelectedIndex && index !== lastSelectedIndex;
    }

    return !(
      (index > firstSelectedIndex && index < lastSelectedIndex) ||
      selectedScreens[index - 1] ||
      selectedScreens[index + 1]
    );
  };
  const handleNavigateToCreateRotationSections = (index: number) => {
    navigate({
      pathname: PATHS.ASSEMBLY_LAYOUTS_CREATE_ROTATION_SECTIONS(props.layoutId),
      search: `?screen=${index}`,
    });
  };

  const onToggleScreenRotationDrawer = () => {
    setOpenScreenRotationDrawer((isOpen: boolean) => !isOpen);
  };

  const isScreensFilled = props.screens.every(
    (screen) => !screen.name.includes('placeholder'),
  );

  const { hasCOD, hasThankYou, hasDYR } = checkSectionTypes(
    props?.sectionTypes,
  );

  const handleClickStep = (journeyState: AssemblyJourneys) => {
    setActiveAssemblyJourney(journeyState);
  };

  const isSidebarOptionsDisabled = (index: number) => {
    const screenIndex = String(index + 1);
    const hasCODSection =
      isCurrentScreenWithCOD(toNumber(screenIndex), globalComponentsScreen) &&
      hasCOD;
    const hasThankYouOrDYRSection =
      (isCurrentScreenWithCODThankYou(
        toNumber(screenIndex),
        globalComponentsScreen,
      ) ||
        isCurrentScreenWithDYR(
          toNumber(screenIndex),
          globalComponentsScreen,
        )) &&
      (hasThankYou || hasDYR);

    return hasCODSection || hasThankYouOrDYRSection;
  };

  return (
    <Container>
      <LayoutEditSidebarAccordion
        formMethods={props.formMethods}
        sectionTypes={props.sectionTypes}
        isScreensFilled={isScreensFilled}
        noTakeoverSelected={noTakeoverSelected}
        hasScreenRotationEvent={hasScreenRotationEvent}
        onToggleDefaultDrawer={onToggleDefaultDrawer}
        onToggleTakeoverDrawer={onToggleTakeoverDrawer}
        onToggleScreenRotationDrawer={onToggleScreenRotationDrawer}
      />
      <LayoutEditSidebarDrawer
        formMethods={props.formMethods}
        screens={props.screens}
        openDefaultDrawer={openDefaultDrawer}
        onToggleDefaultDrawer={onToggleDefaultDrawer}
        handleNavigateToCreateSections={handleNavigateToCreateSections}
        isCODLayout={hasCOD}
        isScreensFilled={isScreensFilled}
        activeAssemblyJourney={props.activeAssemblyJourney}
        handleClickStep={handleClickStep}
        openTakeoverDrawer={openTakeoverDrawer}
        onToggleTakeoverDrawer={onToggleTakeoverDrawer}
        isScreenDisabled={isScreenDisabled}
        isSidebarOptionsDisabled={isSidebarOptionsDisabled}
        openScreenRotationDrawer={openScreenRotationDrawer}
        onToggleScreenRotationDrawer={onToggleScreenRotationDrawer}
        handleNavigateToCreateRotationSections={
          handleNavigateToCreateRotationSections
        }
        screenNames={props.screenNames}
        screenDisplayOrder={props.screenDisplayOrder}
      />
    </Container>
  );
};
